/**
 * Trello-style drop landing: after a card drag ends, a fixed copy of the card
 * animates from the held card's last position into wherever the card
 * rendered, which is correct for moves, cancels, and rejected moves alike.
 * A fixed copy (like Trello's drop overlay) is not clipped by column scrollers.
 */

import { useCallback, useLayoutEffect, useRef, type RefObject } from 'react';
import {
  KANBAN_DRAG_EASING,
  KANBAN_DROP_LANDING_MS,
  KANBAN_FLIP_ANIMATION_ID,
  KANBAN_LANDING_ANIMATION_ID,
  getLandingKeyframes,
  prefersReducedMotion,
} from './Kanban.motion';

interface PendingLanding {
  itemId: string;
  from: { left: number; top: number };
}

/** Same stacking level dnd-kit uses for DragOverlay. */
const LANDING_Z_INDEX = '999';

function createLandingCopy(element: HTMLElement, rect: DOMRect): HTMLElement {
  const copy = element.cloneNode(true) as HTMLElement;
  copy.removeAttribute('data-kanban-flip');
  copy.removeAttribute('tabindex');
  copy.setAttribute('aria-hidden', 'true');
  Object.assign(copy.style, {
    position: 'fixed',
    left: `${rect.left}px`,
    top: `${rect.top}px`,
    width: `${rect.width}px`,
    height: `${rect.height}px`,
    margin: '0',
    zIndex: LANDING_Z_INDEX,
    pointerEvents: 'none',
  });
  return copy;
}

export function useKanbanDropLanding(rootRef: RefObject<HTMLElement | null>) {
  const pendingRef = useRef<PendingLanding | null>(null);

  const land = useCallback(
    (itemId: string, from: { left: number; top: number } | null) => {
      if (!from || prefersReducedMotion()) return;
      pendingRef.current = { itemId, from };
    },
    []
  );

  useLayoutEffect(() => {
    const pending = pendingRef.current;
    const root = rootRef.current;
    if (!pending || !root) return;
    pendingRef.current = null;
    const element = root.querySelector<HTMLElement>(
      `[data-kanban-flip="${CSS.escape(pending.itemId)}"]`
    );
    if (!element) return;
    for (const animation of element.getAnimations()) {
      if (animation.id === KANBAN_FLIP_ANIMATION_ID) animation.cancel();
    }

    const rect = element.getBoundingClientRect();
    const copy = createLandingCopy(element, rect);
    root.appendChild(copy);
    const timing = {
      duration: KANBAN_DROP_LANDING_MS,
      easing: KANBAN_DRAG_EASING,
      id: KANBAN_LANDING_ANIMATION_ID,
    };
    element.animate([{ opacity: 0 }, { opacity: 0 }], timing);
    const landing = copy.animate(getLandingKeyframes(pending.from, rect), timing);
    const removeCopy = () => copy.remove();
    landing.onfinish = removeCopy;
    landing.oncancel = removeCopy;
  });

  return land;
}
